/* ===== TiKanePage.jsx ===== */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Table, Button, InputGroup, Form, Pagination, Row, Col, Badge
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ProtectedSection from '../components/ProtectedSection';
import tikaneService from '../services/tikaneService';
import TiKaneModal from '../components/TiKaneModal';
import TiKaneMinorModal from '../components/TiKaneMinorModal';
import TransactionTiKaneModal from '../components/TransactionTiKaneModal';
import TiKaneViewModal from '../components/TiKaneViewModal';

// Helpers recherche (accent/casse-insensibles)
const normalize = (str) => {
  if (str == null) return '';
  return String(str)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

const toText = (v) => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(toText).join(' ');
  if (typeof v === 'object' && !(v instanceof Date)) return Object.values(v).map(toText).join(' ');
  return v instanceof Date ? v.toLocaleString() : String(v);
};

export default function TiKanePage() {
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showMinorModal, setShowMinorModal] = useState(false);
  const [editData, setEditData] = useState(null);

  // Modal transactions d’un compte
  const [txModalShow, setTxModalShow] = useState(false);
  const [activeTiKaneId, setActiveTiKaneId] = useState(null);

  // Modal lecture seule
  const [viewModalShow, setViewModalShow] = useState(false);
  const [viewTiKaneId, setViewTiKaneId] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Tri
  const [sort, setSort] = useState('startDate-desc');

  const navigate = useNavigate();

  const getServerMessage = (err, fallback = 'Une erreur est survenue.') =>
    (err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback);

  const fetchAccounts = () => {
    tikaneService
      .list()
      .then(setAccounts)
      .catch(err => {
        if (err.response?.status === 403) {
          navigate('/login');
        } else {
          setErrorMsg(getServerMessage(err, 'Impossible de charger les comptes.'));
        }
      });
  };

  useEffect(fetchAccounts, []);

  // Reset page quand filtre / data / tri changent
  useEffect(() => { setPage(1); }, [search, accounts, sort]);

  // Recherche globale (+ tokens 'mineur/adulte')
  const filtered = useMemo(() => {
    const q = normalize(search);
    if (!q) return accounts;

    return accounts.filter(acc => {
      const isMinor = !!acc.minorId;

      const fullName = `${acc.Client?.firstName || ''} ${acc.Client?.lastName || ''}`;
      const nifClient = acc.Client?.nifCin || '';
      const minorName = `${acc.Minor?.firstName || ''} ${acc.Minor?.lastName || ''}`;
      const parentName = `${acc.Minor?.parent?.firstName || ''} ${acc.Minor?.parent?.lastName || ''}`;
      const parentNif = acc.Minor?.parent?.nifCin || '';

      const typeTokens = isMinor ? 'mineur minor' : 'adulte adult';

      const haystack = normalize(
        [
          typeTokens,
          acc.accountNumber,
          fullName,
          nifClient,
          minorName,
          parentName,
          parentNif,
          new Date(acc.startDate).toLocaleDateString(),
          acc.durationDays,
          parseFloat(acc.menCount).toFixed(2),
          parseFloat(acc.dailyValue).toFixed(2),
          parseFloat(acc.totalValue).toFixed(2),
          parseFloat(acc.finalValue).toFixed(2),
          acc.status
        ].map(toText).join(' ')
      );

      return haystack.includes(q);
    });
  }, [accounts, search]);

  // Tri (client-side)
  const sorted = useMemo(() => {
    const copy = [...filtered];
    const [key, dir] = sort.split('-');

    copy.sort((a, b) => {
      let va;
      let vb;

      switch (key) {
        case 'startDate':
          va = new Date(a.startDate).getTime();
          vb = new Date(b.startDate).getTime();
          break;
        case 'status': {
          const sa = String(a.status || '').toLowerCase();
          const sb = String(b.status || '').toLowerCase();
          va = sa < sb ? -1 : sa > sb ? 1 : 0;
          return dir === 'asc' ? va : -va;
        }
        default:
          va = 0;
          vb = 0;
      }

      return dir === 'asc' ? va - vb : vb - va;
    });

    return copy;
  }, [filtered, sort]);

  // Pagination locale
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;
  const visible = useMemo(
    () => sorted.slice(start, start + pageSize),
    [sorted, start, pageSize]
  );

  // Modals CRUD TiKane
  const handleOpen = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleOpenMinor = () => {
    setShowMinorModal(true);
  };

  const handleDelete = id => {
    if (!window.confirm('Supprimer ce compte ?')) return;
    setErrorMsg('');
    tikaneService
      .remove(id)
      .then(fetchAccounts)
      .catch(err => {
        setErrorMsg(getServerMessage(err, 'La suppression a échoué.'));
      });
  };

  const handleSave = data => {
    const op = data.id
      ? tikaneService.update(data.id, data)
      : tikaneService.create(data);

    op.then(() => {
      setShowModal(false);
      fetchAccounts();
    }).catch(err => {
      setErrorMsg(getServerMessage(err, 'Enregistrement impossible.'));
    });
  };

  const handleSaveMinor = data => {
    tikaneService.create(data)
      .then(() => {
        setShowMinorModal(false);
        fetchAccounts();
      })
      .catch(err => setErrorMsg(getServerMessage(err, 'Enregistrement impossible (mineur).')));
  };

  const renderPagination = () => (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <div className="text-muted">
        Résultats : {total} • Page {current}/{totalPages}
      </div>
      <div className="d-flex align-items-center">
        <Form.Select
          size="sm"
          value={pageSize}
          onChange={e => setPageSize(Number(e.target.value))}
          className="me-2"
          style={{ width: 90 }}
          aria-label="Taille de page"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </Form.Select>
        <Pagination className="mb-0">
          <Pagination.Prev
            disabled={current === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          />
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - current) <= 1)
            .map((p, idx, arr) => {
              const prev = arr[idx - 1];
              const needEllipsis = prev && p - prev > 1;
              return (
                <React.Fragment key={p}>
                  {needEllipsis && <Pagination.Ellipsis disabled />}
                  <Pagination.Item active={p === current} onClick={() => setPage(p)}>
                    {p}
                  </Pagination.Item>
                </React.Fragment>
              );
            })}
          <Pagination.Next
            disabled={current === totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          />
        </Pagination>
      </div>
    </div>
  );

  return (
    <Container className="pt-4">
      {errorMsg && (
        <div className="mb-3">
          <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
            <div>{errorMsg}</div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setErrorMsg('')}
            />
          </div>
        </div>
      )}

      <Row className="mb-3 g-2 align-items-end">
        <Col md={4}><h2>Gestion des comptes Ti Kanè</h2></Col>
        <Col md={4}>
          <InputGroup>
            <Form.Control
              placeholder="Rechercher (type, client, mineur, parent, NIF/CIN, compte, statut, etc.)…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <Button variant="outline-secondary" onClick={() => setSearch('')}>×</Button>
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Label className="mb-1">Trier par</Form.Label>
          <Form.Select size="sm" value={sort} onChange={e => setSort(e.target.value)}>
            <option value="startDate-desc">Plus récent → plus ancien</option>
            <option value="startDate-asc">Plus ancien → plus récent</option>
            <option value="status-asc">Statut A → Z</option>
            <option value="status-desc">Statut Z → A</option>
          </Form.Select>
        </Col>
        <Col md={2} className="text-end">
          <div className="d-flex gap-2 justify-content-end">
            <ProtectedSection code="TIKANE_CREATE">
              <Button onClick={handleOpen}>+ Ouvrir un compte</Button>
            </ProtectedSection>
            <ProtectedSection code="TIKANE_CREATE">
              <Button variant="outline-success" onClick={handleOpenMinor}>+ Compte mineur</Button>
            </ProtectedSection>
          </div>
        </Col>
      </Row>

      <TiKaneModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSave}
        initialData={editData}
      />

      <TiKaneMinorModal
        show={showMinorModal}
        onHide={() => setShowMinorModal(false)}
        onSave={handleSaveMinor}
      />

      <ProtectedSection code="TIKANE_VIEW">
        <Table hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Client / Mineur</th>
              <th>NIF/CIN (ou Parent)</th>
              <th>N° Compte</th>
              <th>Ouvert le</th>
              <th>Durée/j</th>
              <th>Men</th>
              <th>Valeur/j</th>
              <th>Total</th>
              <th>Restant</th>
              <th>Statut</th>
              <th style={{ width: 380 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(acc => {
              const isMinor = !!acc.minorId;
              const clientName = `${acc.Client?.firstName || ''} ${acc.Client?.lastName || ''}`.trim();
              const minorName = `${acc.Minor?.firstName || ''} ${acc.Minor?.lastName || ''}`.trim();
              const parentName = `${acc.Minor?.parent?.firstName || ''} ${acc.Minor?.parent?.lastName || ''}`.trim();
              const nifClient = acc.Client?.nifCin || '—';

              return (
                <tr key={acc.id}>
                  <td>
                    {isMinor
                      ? <Badge bg="warning" text="dark">Mineur</Badge>
                      : <Badge bg="info" text="dark">Adulte</Badge>}
                  </td>
                  <td>{isMinor ? (minorName || '—') : (clientName || '—')}</td>
                  <td>{isMinor ? (parentName || '—') : (nifClient || '—')}</td>
                  <td>{acc.accountNumber}</td>
                  <td>{new Date(acc.startDate).toLocaleDateString()}</td>
                  <td>{acc.durationDays}</td>
                  <td>{parseFloat(acc.menCount).toFixed(2)}</td>
                  <td>{parseFloat(acc.dailyValue).toFixed(2)}</td>
                  <td>{parseFloat(acc.totalValue).toFixed(2)}</td>
                  <td>{parseFloat(acc.finalValue).toFixed(2)}</td>
                  <td>{acc.status}</td>
                  <td>
                    <ProtectedSection code="TIKANE_VIEW">
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setViewTiKaneId(acc.id);
                          setViewModalShow(true);
                        }}
                      >
                        Voir
                      </Button>
                    </ProtectedSection>
                    <ProtectedSection code="TIKANE_UPDATE">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => setEditData(acc) || setShowModal(true)}
                      >
                        Modifier
                      </Button>
                    </ProtectedSection>
                    <ProtectedSection code="TIKANE_DELETE">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="me-2"
                        onClick={() => handleDelete(acc.id)}
                      >
                        Supprimer
                      </Button>
                    </ProtectedSection>
                    <ProtectedSection code="TRANS_VIEW">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          setActiveTiKaneId(acc.id);
                          setTxModalShow(true);
                        }}
                      >
                        Transactions
                      </Button>
                    </ProtectedSection>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        {sorted.length === 0 && (
          <p className="text-center text-muted">Aucun compte trouvé.</p>
        )}
        {sorted.length > 0 && renderPagination()}

        <TransactionTiKaneModal
          tiKaneId={activeTiKaneId}
          show={txModalShow}
          onHide={() => setTxModalShow(false)}
        />

        <TiKaneViewModal
          show={viewModalShow}
          onHide={() => setViewModalShow(false)}
          tiKaneId={viewTiKaneId}
        />
      </ProtectedSection>
    </Container>
  );
}